import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useRepository } from "../shell-data";
import { useRepoMaybe } from "../repo";

/** The repository's default branch: what views show when no branch is picked. */
export function useDefaultBranch(): string {
  const repo = useRepoMaybe();
  return useRepository(repo?.key ?? "").data?.defaultBranch || "main";
}

/** The branch every view is looking at. */
/** A ref as people read it: tags without their `tags/` prefix. */
export const refLabel = (ref: string) => (ref.startsWith("tags/") ? ref.slice(5) : ref);

export function useRefName(): string {
  const s = useSearch({ strict: false }) as { ref?: string };
  const fallback = useDefaultBranch();
  return s.ref ?? fallback;
}

export type Theme = "system" | "light" | "dark";
const KEY = "gd.console.theme";

function stored(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(stored);
  useEffect(() => {
    if (theme === "system") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      // Not remembered across visits without storage; it still applies now.
    }
  }, [theme]);
  return [theme, setTheme];
}

// A "g" was pressed and the next key goes somewhere ("g i" opens Issues).
let chordAt = 0;
export const startChord = () => void (chordAt = Date.now());
/** Ends a pending "g" chord; true if one was pending. */
export function endChord(): boolean {
  const pending = Date.now() - chordAt < 1200;
  chordAt = 0;
  return pending;
}

/**
 * Whether a view may treat this key as its own shortcut: not typed into a field, not with a
 * modifier, and not the second key of a "g" chord that the shell handles.
 */
export function shortcutFree(e: KeyboardEvent): boolean {
  return !typing(e) && !e.metaKey && !e.ctrlKey && !e.altKey && !e.defaultPrevented && Date.now() - chordAt >= 1200;
}

/** Whether focus is in a place where typing means text, not a shortcut. */
export function typing(e: KeyboardEvent): boolean {
  const t = e.target;
  if (!(t instanceof HTMLElement)) return false;
  return t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || !!t.closest("[role=dialog], form");
}

export const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const modKey = isMac ? "⌘" : "Ctrl";
